import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";

const links = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Fleet", href: "#fleet" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-[0_4px_24px_-12px_rgba(13,27,64,0.18)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-[78px] flex items-center justify-between">
        <a href="#home" className="flex items-center" data-testid="navbar-logo">
          <Logo dark={scrolled} />
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={`text-[14px] font-medium tracking-wide transition-colors ${
                scrolled ? "text-[#0D1B40] hover:text-[#F5A623]" : "text-white/90 hover:text-[#F5A623]"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:block">
          <a
            href="#contact"
            data-testid="navbar-quote-cta"
            className="btn-primary text-[14px]"
            style={{ padding: "11px 22px" }}
          >
            Get a Quote
          </a>
        </div>

        <button
          className="md:hidden p-2 rounded-md"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="navbar-mobile-toggle"
        >
          {open ? (
            <X size={26} color={scrolled ? "#0D1B40" : "#fff"} />
          ) : (
            <Menu size={26} color={scrolled ? "#0D1B40" : "#fff"} />
          )}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-slate-100 shadow-lg" data-testid="navbar-mobile-menu">
          <div className="px-5 py-4 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-[#0D1B40] font-medium text-[15px]"
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              className="btn-primary justify-center"
              data-testid="mobile-navbar-quote-cta"
            >
              Get a Quote
            </a>
          </div>
        </div>
      )}
    </header>
  );
}

function Logo({ dark }) {
  // When the navbar is over the hero (transparent / on dark image), show a white-bg pill so the
  // teal+yellow logo stays vivid. Once scrolled (white nav), drop the pill.
  return (
    <div
      className={`flex items-center transition-all duration-300 ${
        dark ? "" : "bg-white rounded-lg pl-2 pr-3 py-1.5 shadow-md"
      }`}
    >
      <img
        src="/brand/rydey-logo.png"
        alt="Rydey - All Day Everyday"
        className={`transition-all duration-300 ${dark ? "h-12" : "h-9"} w-auto`}
        data-testid="rydey-logo-img"
      />
    </div>
  );
}
